import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { transferTicket, type TransferTicketInput } from '@/lib/api/tickets.js';
import { listStores } from '@/lib/api/stores.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function TransferTicketModal({
  open,
  ticketId,
  ticketNumber,
  onClose,
  onSuccess,
}: {
  open: boolean;
  ticketId: string;
  ticketNumber: string;
  onClose: () => void;
  onSuccess: (result?: { newTicketId?: string }) => void;
}) {
  const user = useAuthStore((s) => s.user);
  const [destinationStoreId, setDestinationStoreId] = useState('');
  const [reason, setReason] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const storesQuery = useQuery({
    queryKey: ['stores-list'],
    queryFn: listStores,
    enabled: open,
  });

  const destinationStores = (storesQuery.data ?? []).filter(
    (s) => s.active && s.id !== user?.storeId,
  );

  useEffect(() => {
    if (open) {
      setDestinationStoreId('');
      setReason('');
      setNotifyCustomer(true);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: TransferTicketInput) => transferTicket(ticketId, body),
    onSuccess: (result) => {
      toastSuccess(
        result.newTicketNumber
          ? `Ticket transferred — new ticket ${result.newTicketNumber}`
          : 'Ticket transferred successfully',
      );
      onSuccess({ newTicketId: result.newTicketId });
      onClose();
    },
    onError: (error) => {
      toastError(parseApiError(error).message);
    },
  });

  if (!open) return null;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!destinationStoreId) {
      toastError('Select a destination store');
      return;
    }
    mutation.mutate({
      destinationStoreId,
      reason: reason.trim() || undefined,
      notifyCustomer,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-950/40"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Transfer Ticket</h2>
            <p className="mt-1 text-sm text-slate-500">{ticketNumber}</p>
          </div>
          <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-slate-700">
            Destination store
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={destinationStoreId}
              onChange={(e) => setDestinationStoreId(e.target.value)}
              required
            >
              <option value="">Select store…</option>
              {destinationStores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Reason (optional)
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this ticket being transferred?"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
            />
            Notify customer about the transfer
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
            >
              {mutation.isPending ? 'Transferring…' : 'Transfer Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
